import { csvAmount, csvDate, toCsv } from './csv';

/** Einzelbuchung, wie `/admin/report/postings` sie liefert. */
export interface ExportPosting {
  stichtag: string;
  voucherDate?: string;
  voucherNumber?: string;
  partner?: string;
  text?: string;
  /** netto (D-21) */
  amountCents: number;
  taxCents?: number;
  grossCents?: number;
  accountNumber?: string;
  accountName?: string;
  groupName?: string;
  costCentreNumber?: string;
  costCentreName?: string;
  divisionId?: string;
  taxArea?: string;
  /** Tag aus SevDesk, z.B. eine Veranstaltung */
  tagName?: string;
}

export interface ExportGroup {
  name: string;
  side: 'einnahme' | 'ausgabe';
  cents: number;
  priorCents: number;
  accounts: { number: string; name: string; cents: number }[];
}

/**
 * Dateiname mit Zeitraum und ggf. Abteilung, Bereich und Drill-down-Titel, z. B.
 * "mybudget-buchungen-2026-01-01_2026-10-25-Beiträge.csv". Umlaute bleiben, alles andere außer
 * Buchstaben, Ziffern, Punkt, Unterstrich und Bindestrich wird zu "_".
 */
export function csvName(kind: string, query: Record<string, string>, extra?: string): string {
  const parts = [
    'mybudget',
    kind,
    `${query.from}_${query.to}`,
    query.division,
    query.taxArea,
    query.tag,
    extra,
  ];
  const name = parts
    .filter(Boolean)
    .join('-')
    .replace(/[^\p{L}\p{N}._-]+/gu, '_');
  return `${name}.csv`;
}

/** FR-30 / FR-26: alle Spalten des Drill-downs, ein Posten je Zeile. */
export function postingsCsv(rows: ExportPosting[], divisionNames: Record<string, string>): string {
  return toCsv(
    [
      'Zahlungsdatum',
      'Belegdatum',
      'Belegnummer',
      'Partner',
      'Buchungstext',
      'Abteilung',
      'Steuerlicher Bereich',
      'Kostenstelle',
      'Kostenstellen-Bezeichnung',
      '#Tag',
      'Konto',
      'Kontobezeichnung',
      'Gruppe',
      'Brutto',
      'USt',
      'Netto',
    ],
    rows.map((r) => [
      csvDate(r.stichtag),
      csvDate(r.voucherDate),
      r.voucherNumber,
      r.partner,
      r.text,
      r.divisionId ? (divisionNames[r.divisionId] ?? r.divisionId) : '',
      r.taxArea,
      r.costCentreNumber,
      r.costCentreName,
      r.tagName,
      r.accountNumber,
      r.accountName,
      r.groupName,
      // Wie auf der Seite "Umsatzsteuer": Brutto + USt = Netto (USt aus Sicht des Finanzamts).
      csvAmount(r.grossCents ?? r.amountCents),
      csvAmount(-(r.taxCents ?? 0)),
      csvAmount(r.amountCents),
    ]),
    [13, 14, 15],
  );
}

/**
 * FR-30: Summenansicht wie auf dem Dashboard -- je Gruppe eine Zeile, darunter ihre Konten,
 * am Ende Einnahmen, Ausgaben und Saldo.
 */
export function summaryCsv(
  groups: ExportGroup[],
  totals: { income: number; expense: number; saldo: number },
  prior: { income: number; expense: number; saldo: number },
): string {
  const rows: (string | undefined)[][] = [];
  for (const side of ['einnahme', 'ausgabe'] as const) {
    const label = side === 'einnahme' ? 'Einnahmen' : 'Ausgaben';
    for (const g of groups.filter((x) => x.side === side)) {
      rows.push([label, g.name, '', '', csvAmount(g.cents), csvAmount(g.priorCents)]);
      for (const a of g.accounts) {
        rows.push([label, g.name, a.number, a.name, csvAmount(a.cents), '']);
      }
    }
  }
  rows.push(['Summe', 'Einnahmen', '', '', csvAmount(totals.income), csvAmount(prior.income)]);
  rows.push(['Summe', 'Ausgaben', '', '', csvAmount(totals.expense), csvAmount(prior.expense)]);
  rows.push(['Summe', 'Saldo', '', '', csvAmount(totals.saldo), csvAmount(prior.saldo)]);
  return toCsv(
    ['Seite', 'Gruppe', 'Konto', 'Kontobezeichnung', 'Netto', 'Vorjahr netto'],
    rows,
    [4, 5],
  );
}

/** Netto, Umsatzsteuer, Brutto in Cent wie `/admin/report/vat` (USt = Brutto − Netto). */
export interface ExportAmounts {
  net: number;
  tax: number;
  gross: number;
}

/**
 * D-21: Umsatzsteuer-Seite als CSV, Zeilen wie auf der Seite -- Brutto + USt = Netto, die USt
 * also aus Sicht des Finanzamts (eingenommene USt negativ, Vorsteuer positiv).
 */
export function vatCsv(vat: {
  divisions: { name: string; income: ExportAmounts; expense: ExportAmounts }[];
  income: ExportAmounts;
  expense: ExportAmounts;
  finanzamt: { refunds: number; payments: number };
  saldo: ExportAmounts;
}): string {
  const line = (bereich: string, zeile: string, a: ExportAmounts) => [
    bereich,
    zeile,
    csvAmount(a.gross),
    csvAmount(-a.tax),
    csvAmount(a.net),
  ];
  const fa = (cents: number) => ({ gross: cents, tax: 0, net: cents });
  const rows = [
    line('Verein gesamt', 'Einnahmen', vat.income),
    line('Verein gesamt', 'Ausgaben', vat.expense),
    line('Verein gesamt', 'Vom Finanzamt erstattet', fa(vat.finanzamt.refunds)),
    line('Verein gesamt', 'An das Finanzamt gezahlt', fa(vat.finanzamt.payments)),
    line('Verein gesamt', 'Saldo', vat.saldo),
    ...vat.divisions.flatMap((d) => [
      line(d.name, 'Einnahmen', d.income),
      line(d.name, 'Ausgaben', d.expense),
    ]),
  ];
  return toCsv(['Bereich', 'Zeile', 'Brutto', 'Umsatzsteuer', 'Netto'], rows, [2, 3, 4]);
}
